/**
 * @fileoverview Walkthrough Context Provider
 * @description Manages global walkthrough state with localStorage persistence.
 * Provides state and actions to all walkthrough consumers via React Context.
 * @module features/walkthrough/context/WalkthroughContext
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import type {
  WalkthroughState,
  WalkthroughActions,
  WalkthroughProgress,
  WalkthroughDefinition,
} from '../types';
import { getWalkthrough } from '../walkthrough-registry';
import { logger } from '@/lib/logger';

const STORAGE_KEY = 'flowstate_walkthroughs';

type WalkthroughContextValue = WalkthroughState & WalkthroughActions;

const WalkthroughContext = createContext<WalkthroughContextValue | null>(null);

function loadProgress(): Record<string, WalkthroughProgress> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    logger.error('Error loading walkthrough progress:', { error });
  }
  return {};
}

function saveProgress(allProgress: Record<string, WalkthroughProgress>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allProgress));
  } catch (error) {
    logger.error('Error saving walkthrough progress:', { error });
  }
}

export function WalkthroughProvider({ children }: { children: React.ReactNode }) {
  const [allProgress, setAllProgress] = useState<Record<string, WalkthroughProgress>>({});
  const [activeWalkthrough, setActiveWalkthrough] = useState<WalkthroughDefinition | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load persisted state on mount
  useEffect(() => {
    setAllProgress(loadProgress());
    setIsLoading(false);
  }, []);

  // Persist state whenever it changes
  const updateProgress = useCallback(
    (walkthroughId: string, updater: (prev: WalkthroughProgress) => WalkthroughProgress) => {
      setAllProgress(prev => {
        const current = prev[walkthroughId] || {
          walkthroughId,
          status: 'not_started' as const,
          completedSteps: [],
          currentStepIndex: 0,
          version: 0,
        };
        const updated = updater(current);
        const next = { ...prev, [walkthroughId]: updated };
        saveProgress(next);
        return next;
      });
    },
    []
  );

  const activeProgress = activeWalkthrough
    ? allProgress[activeWalkthrough.id] || null
    : null;

  const startWalkthrough = useCallback(
    (walkthroughId: string) => {
      const definition = getWalkthrough(walkthroughId);
      if (!definition) {
        logger.error(`Walkthrough "${walkthroughId}" not found in registry`);
        return;
      }

      setActiveWalkthrough(definition);
      updateProgress(walkthroughId, prev => {
        // If already completed at the same version, don't restart
        if (prev.status === 'completed' && prev.version === definition.version) {
          return prev;
        }
        // Resume in-progress tours instead of wiping the step index
        // (startIfNotCompleted / page effects can re-invoke start while active).
        if (prev.status === 'in_progress' && prev.walkthroughId === walkthroughId) {
          return prev;
        }
        return {
          ...prev,
          walkthroughId,
          status: 'in_progress',
          currentStepIndex: 0,
          completedSteps: [],
          startedAt: new Date().toISOString(),
          finishedAt: undefined,
          version: definition.version,
        };
      });
    },
    [updateProgress]
  );

  const nextStep = useCallback(() => {
    if (!activeWalkthrough) {return;}
    const maxIndex = activeWalkthrough.steps.length - 1;

    updateProgress(activeWalkthrough.id, prev => {
      const nextIndex = Math.min(prev.currentStepIndex + 1, maxIndex);
      const currentStep = activeWalkthrough.steps[prev.currentStepIndex];
      const completedSteps = currentStep
        ? [...new Set([...prev.completedSteps, currentStep.id])]
        : prev.completedSteps;

      if (nextIndex > maxIndex || prev.currentStepIndex === maxIndex) {
        // Reached the end
        setActiveWalkthrough(null);
        return {
          ...prev,
          status: 'completed',
          completedSteps: activeWalkthrough.steps.map(s => s.id),
          currentStepIndex: maxIndex,
          finishedAt: new Date().toISOString(),
        };
      }

      return { ...prev, currentStepIndex: nextIndex, completedSteps };
    });
  }, [activeWalkthrough, updateProgress]);

  const prevStep = useCallback(() => {
    if (!activeWalkthrough) {return;}
    updateProgress(activeWalkthrough.id, prev => ({
      ...prev,
      currentStepIndex: Math.max(prev.currentStepIndex - 1, 0),
    }));
  }, [activeWalkthrough, updateProgress]);

  const skipStep = useCallback(() => {
    nextStep();
  }, [nextStep]);

  const completeStep = useCallback(
    (stepId: string) => {
      if (!activeWalkthrough) {return;}
      updateProgress(activeWalkthrough.id, prev => ({
        ...prev,
        completedSteps: [...new Set([...prev.completedSteps, stepId])],
      }));
    },
    [activeWalkthrough, updateProgress]
  );

  const dismissWalkthrough = useCallback(() => {
    if (!activeWalkthrough) {return;}
    updateProgress(activeWalkthrough.id, prev => ({
      ...prev,
      status: 'dismissed',
      finishedAt: new Date().toISOString(),
    }));
    setActiveWalkthrough(null);
  }, [activeWalkthrough, updateProgress]);

  const completeWalkthrough = useCallback(() => {
    if (!activeWalkthrough) {return;}
    updateProgress(activeWalkthrough.id, prev => ({
      ...prev,
      status: 'completed',
      completedSteps: activeWalkthrough.steps.map(s => s.id),
      finishedAt: new Date().toISOString(),
    }));
    setActiveWalkthrough(null);
  }, [activeWalkthrough, updateProgress]);

  const resetWalkthrough = useCallback(
    (walkthroughId: string) => {
      setAllProgress(prev => {
        const next = { ...prev };
        delete next[walkthroughId];
        saveProgress(next);
        return next;
      });
      if (activeWalkthrough?.id === walkthroughId) {
        setActiveWalkthrough(null);
      }
    },
    [activeWalkthrough]
  );

  const isWalkthroughCompleted = useCallback(
    (walkthroughId: string) => allProgress[walkthroughId]?.status === 'completed',
    [allProgress]
  );

  const isWalkthroughStarted = useCallback(
    (walkthroughId: string) => {
      const progress = allProgress[walkthroughId];
      return progress?.status === 'in_progress' || progress?.status === 'completed';
    },
    [allProgress]
  );

  const getProgress = useCallback(
    (walkthroughId: string) => allProgress[walkthroughId] || null,
    [allProgress]
  );

  const value = useMemo<WalkthroughContextValue>(
    () => ({
      activeWalkthrough,
      activeProgress,
      allProgress,
      isLoading,
      startWalkthrough,
      nextStep,
      prevStep,
      skipStep,
      completeStep,
      dismissWalkthrough,
      completeWalkthrough,
      resetWalkthrough,
      isWalkthroughCompleted,
      isWalkthroughStarted,
      getProgress,
    }),
    [
      activeWalkthrough,
      activeProgress,
      allProgress,
      isLoading,
      startWalkthrough,
      nextStep,
      prevStep,
      skipStep,
      completeStep,
      dismissWalkthrough,
      completeWalkthrough,
      resetWalkthrough,
      isWalkthroughCompleted,
      isWalkthroughStarted,
      getProgress,
    ]
  );

  return (
    <WalkthroughContext.Provider value={value}>
      {children}
    </WalkthroughContext.Provider>
  );
}

export function useWalkthroughContext(): WalkthroughContextValue {
  const ctx = useContext(WalkthroughContext);
  if (!ctx) {
    throw new Error('useWalkthroughContext must be used within a WalkthroughProvider');
  }
  return ctx;
}

export default WalkthroughContext;
